/**
 * Turnaround: how long a sample has been in the lab. The clock starts at the USPS lab-arrival
 * timestamp (the sidecar's `receivedAt`, when `receivedBasis === 'scan'`) and runs to "now", so the
 * Review list shows a live age in days / hours / minutes next to each member. Pure + display-only:
 * nothing here is written to the sidecar or the result JSON (it is recomputed on every render tick).
 */

export interface Turnaround {
  /** Elapsed milliseconds, clamped to >= 0 (a future arrival from clock skew reads as 0). */
  ms: number;
  days: number;
  /** 0-23 within the day. */
  hours: number;
  /** 0-59 within the hour. */
  minutes: number;
  /** Compact label: "11d 4h 9m" / "4h 9m" / "9m" / "0m" (units below the leading one are dropped). */
  text: string;
}

/** Parse an ISO-8601 timestamp to epoch ms, or null when absent / unparseable. */
export function parseTs(ts: string | null | undefined): number | null {
  if (!ts) return null;
  // A bare 'YYYY-MM-DD' parses as UTC midnight, which skews both the displayed day (it can render
  // as the previous day west of UTC) and the d/h/m age by the local offset. Real scan arrivals carry
  // a full ISO instant with 'Z' and are unaffected; this only anchors a date-only value to local noon.
  const s = /^\d{4}-\d{2}-\d{2}$/.test(ts) ? `${ts}T12:00:00` : ts;
  const ms = Date.parse(s);
  return Number.isFinite(ms) ? ms : null;
}

/** Format a millisecond span as "11d 0h 9m" / "4h 9m" / "9m" / "0m" (units below the leading one drop). */
export function formatDuration(ms: number): string {
  const totalMin = Math.floor(Math.max(0, ms) / 60_000);
  const days = Math.floor(totalMin / 1_440);
  const hours = Math.floor((totalMin % 1_440) / 60);
  const minutes = totalMin % 60;
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (days || hours) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  return parts.join(' ');
}

/**
 * Elapsed time from `fromTs` to `nowMs`, as d/h/m. Returns null when `fromTs` is missing or
 * unparseable (the caller shows a "no USPS scan" placeholder). A negative span (arrival in the
 * future) clamps to 0 so the badge never shows a negative age.
 */
export function turnaround(fromTs: string | null | undefined, nowMs: number): Turnaround | null {
  const from = parseTs(fromTs);
  if (from == null) return null;
  const ms = Math.max(0, nowMs - from);
  const totalMin = Math.floor(ms / 60_000);
  const days = Math.floor(totalMin / 1_440);
  const hours = Math.floor((totalMin % 1_440) / 60);
  const minutes = totalMin % 60;
  return { ms, days, hours, minutes, text: formatDuration(ms) };
}

const ARRIVAL_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const CLOCK_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
});

/** Friendly local arrival stamp, e.g. "Jun 13, 4:00 PM". Falls back to the raw string if unparseable. */
export function fmtArrival(ts: string | null | undefined): string | null {
  const ms = parseTs(ts);
  if (ms == null) return ts ? String(ts) : null;
  return ARRIVAL_FMT.format(ms);
}

/** The live wall clock for the header, e.g. "Jun 13, 4:00:56 PM". */
export function fmtClock(nowMs: number): string {
  return CLOCK_FMT.format(nowMs);
}
